import {t} from '@lingui/macro';
import _ from 'lodash';
import memoize from 'lru-memoize';
import {DateTime} from 'luxon';
import React, {Component, createRef, RefObject} from 'react';
import {Date as SugarDate} from 'sugar';
import {Key} from 'ts-key-enum';

import {
  forwardInteractionComponentProps,
  InteractionComponentProps,
} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {FrontKeyboardEvent, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputChildProps} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../srcCommon/internationalization/internationalizationContext';
import {formatTime, TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {IncrementMinutesValues, TimeDropdown} from './timeDropdown';

/*
 * Props.
 */

interface TimeComboBoxProps extends InternationalizationContextProps, InteractionComponentProps {
  timeFormat: TimeFormatsEnum;
  selectedTime?: DateTime;
  onTimeSelect?: HandlerOf<DateTime | undefined>;
  minTime?: DateTime;
  maxTime?: DateTime;
  incrementMinutes?: IncrementMinutesValues;
  /** Allow style customization. */
  className?: string;
  noMargin?: boolean;
  emptyValue?: string;
  dropdownWidth?: number;
  showChevron?: boolean;
}

/*
 * Component.
 */

interface TimeComboBoxState {
  isInputFocused: boolean;
  value: string;
}

class TimeComboBoxComponent extends Component<TimeComboBoxProps, TimeComboBoxState> {
  constructor(props: TimeComboBoxProps) {
    super(props);

    const value = props.selectedTime
      ? formatTime(props.localize, props.timeFormat, props.selectedTime)
      : props.emptyValue || '';

    this.state = {isInputFocused: false, value};

    this.inputRef = createRef<BoxedInput>();
    this.boxRef = createRef<HTMLDivElement>();
  }

  private readonly inputRef: RefObject<BoxedInput>;
  private readonly boxRef: RefObject<HTMLDivElement>;

  /*
   * Input event handlers.
   */

  private onChange: HandlerOf<string> = (value) => {
    this.setState({value});
  };

  private onFocus = () => {
    this.setState({isInputFocused: true});
  };

  private onKeyDown: HandlerOf<FrontKeyboardEvent> = (event) => {
    if (event.key !== Key.Enter) {
      return;
    }

    if (!this.inputRef.current) {
      return;
    }

    this.inputRef.current.blur();
  };

  private onBlur = () => {
    this.setState(({value}) => {
      const {localize, timeFormat, emptyValue} = this.props;

      if (value === emptyValue) {
        return {isInputFocused: false, value};
      }

      // Parse the current value.
      const parsedTime = this.parseValue(value);
      const newValue = (parsedTime && formatTime(localize, timeFormat, parsedTime)) || value;

      // Close the dropdown and clean the value.
      return {isInputFocused: false, value: newValue};
    });
  };

  private readonly parseValue = memoize()((value: string) => {
    const {i18n, selectedTime} = this.props;

    const sugarDate = SugarDate.create(value, {locale: i18n.language});
    // If we have an existing selectedTime, restore the year/month/day values since SugarDate uses the current year/month/day.
    const existingDateProperties = selectedTime
      ? {year: selectedTime.year, month: selectedTime.month, day: selectedTime.day}
      : {};
    const parsedTime = DateTime.fromJSDate(sugarDate).set(existingDateProperties);
    return (parsedTime.isValid && parsedTime) || undefined;
  });

  /*
   * Dropdown event handlers.
   */

  private readonly onTimeSelect: HandlerOf<DateTime | undefined> = (time) => {
    // We need a ref to the input at this point.
    const currentInput = this.inputRef.current;
    if (!currentInput) {
      return;
    }

    if (!time && this.props.emptyValue) {
      this.onChange(this.props.emptyValue);
      currentInput.blur();
      return;
    }

    // Get a string value for provided time.
    const timeString = formatTime(this.props.localize, this.props.timeFormat, time);

    // Blur the input and update its value.
    this.onChange(timeString);
    currentInput.blur();
  };

  componentDidUpdate(_prevProps: TimeComboBoxProps, prevState: TimeComboBoxState) {
    const {onTimeSelect} = this.props;
    const {value, isInputFocused} = this.state;
    const inputLostFocus = prevState.isInputFocused && !isInputFocused;

    if (inputLostFocus && _.isString(value)) {
      const parsedTime = this.parseValue(value);
      if (onTimeSelect) {
        onTimeSelect(parsedTime || undefined);
      }
    }
  }

  /*
   * Render.
   */

  render() {
    return (
      <>
        <BoxedInput
          ref={this.inputRef}
          boxRef={this.boxRef}
          boxClassName={this.props.className}
          noMargin={this.props.noMargin}
        >
          {(childProps) => (
            <>
              {this.renderInputContent(childProps)}
              {this.props.showChevron && <BoxedInputIcon {...childProps} iconName="chevronDownSmall" />}
            </>
          )}
        </BoxedInput>
        {this.state.isInputFocused && this.renderDropdown()}
      </>
    );
  }

  private renderInputContent(childProps: BoxedInputChildProps) {
    const {i18n} = this.props;
    return (
      <BoxedInputElement
        size={childProps.size}
        isDisabled={childProps.isDisabled}
        inputId={childProps.inputId}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyDown={this.onKeyDown}
        placeholder={i18n._(t('time-combobox-placeholder')`hh:mm`)}
        type={BoxedInputElementTypesEnum.TEXT}
        value={this.state.value}
        {...forwardInteractionComponentProps(this.props)}
      />
    );
  }

  private renderDropdown() {
    return (
      <TimeDropdown
        width={this.props.dropdownWidth}
        timeFormat={this.props.timeFormat}
        selectedTime={this.props.selectedTime}
        minTime={this.props.minTime}
        maxTime={this.props.maxTime}
        incrementMinutes={this.props.incrementMinutes}
        emptyValue={this.props.emptyValue}
        anchor={this.boxRef}
        onTimeSelect={this.onTimeSelect}
        isExclusive={false}
      />
    );
  }
}

export const TimeComboBox = withInternationalization(TimeComboBoxComponent);
